import React from "react";
import { MapPin, Polygon, Users } from "@phosphor-icons/react";
import { PlanListItem, PlanTenantGroup } from "./planTenants";

interface PlanTenantListProps {
  groups: PlanTenantGroup[];
  /** Locate (highlight + jump to its page) a placed unit/garage on the plan. */
  onLocate: (item: PlanListItem) => void;
}

/**
 * Read-only list shown under the plan: every tenant with the units/garages they hold on this
 * plan. Tenants renting several get a count badge so they stand out; clicking a chip highlights
 * the placement on the canvas. Renders nothing when no units/garages are placed yet.
 */
export const PlanTenantList: React.FC<PlanTenantListProps> = ({ groups, onLocate }) => {
  if (groups.length === 0) return null;

  const tenantCount = groups.filter((g) => !g.isUnassigned).length;

  return (
    <div className="rounded-lg border border-neutral-primary/15 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Users size={18} weight="duotone" className="text-neutral-secondary-mid" />
        <h3 className="text-sm font-semibold text-neutral-primary">Huurders op deze plattegrond</h3>
        <span className="text-xs text-neutral-secondary-mid">
          {tenantCount} {tenantCount === 1 ? "huurder" : "huurders"}
        </span>
      </div>

      <ul className="space-y-2.5">
        {groups.map((group) => (
          <li
            key={group.tenant}
            className="flex flex-col gap-1.5 border-t border-neutral-primary/10 pt-2.5 first:border-t-0 first:pt-0 sm:flex-row sm:items-start sm:gap-3"
          >
            <div className="flex shrink-0 items-center gap-2 sm:w-56">
              <span
                className={`truncate text-sm font-medium ${
                  group.isUnassigned ? "italic text-neutral-secondary-mid" : "text-neutral-primary"
                }`}
                title={group.tenant}
              >
                {group.tenant}
              </span>
              {!group.isUnassigned && group.items.length > 1 && (
                <span
                  className="shrink-0 rounded-full bg-brick-primary/10 px-2 py-0.5 text-[11px] font-semibold text-brick-primary"
                  title={`${group.items.length} eenheden gehuurd`}
                >
                  {group.items.length}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap gap-1.5">
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onLocate(item)}
                    title="Toon op de plattegrond"
                    className="flex items-center gap-1.5 rounded-full border border-neutral-primary/15 bg-neutral-50 px-2 py-1 text-xs text-neutral-primary hover:border-brick-primary hover:bg-brick-primary/5"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-medium">{item.label}</span>
                    {item.shapes.includes("contour") && (
                      <Polygon size={13} weight="bold" className="text-neutral-secondary-mid" />
                    )}
                    {item.shapes.includes("pin") && (
                      <MapPin size={13} weight="fill" className="text-neutral-secondary-mid" />
                    )}
                  </button>
                ))}
              </div>
              {group.rentedUnits.length > 0 && (
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs text-neutral-secondary-mid">
                  <span className="shrink-0">Huurt units:</span>
                  <span className="font-medium text-neutral-primary">
                    {group.rentedUnits.join(" · ")}
                  </span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlanTenantList;
